import type { ReactNode } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Breadcrumbs } from './Breadcrumbs';
import { useViewport } from '../../hooks/useViewport';
import type { NavigationItem, CompanyInfo, SocialLink } from '../../types/navigation';

interface MainLayoutProps {
  children: ReactNode;
  showBreadcrumbs?: boolean;
  showFooter?: boolean;
  className?: string;
}

// Default company information
const defaultCompanyInfo: CompanyInfo = {
  name: 'NIEDO Careers',
  tagline: 'Careers at NIEDO',
};

// Default navigation items
const defaultNavigationItems: NavigationItem[] = [
  { label: 'Home', href: '/' },
  { label: 'Jobs', href: '/jobs' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
  { label: 'Dashboard', href: '/dashboard', requiresAuth: true },
  { label: 'Admin', href: '/admin', requiresAuth: true, adminOnly: true },
];

// Default social links
const defaultSocialLinks: SocialLink[] = [
  { platform: 'linkedin', url: 'https://www.linkedin.com/company/niedo-india', icon: 'linkedin' },
  { platform: 'twitter', url: 'https://x.com/niedoindia', icon: 'twitter' },
  { platform: 'facebook', url: 'https://facebook.com/niedoindia', icon: 'facebook' },
];

// Default contact information
const defaultContactInfo = {
  address: 'NIEDO, India',
  phone: '+91 7006109200',
  email: 'info@niedo.org',
};

export function MainLayout({ 
  children, 
  showBreadcrumbs = true, 
  showFooter = true,
  className = '' 
}: MainLayoutProps) {
  const { isMobile } = useViewport();
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <Header 
        companyInfo={defaultCompanyInfo}
        navigationItems={defaultNavigationItems}
      />

      {/* Breadcrumbs - hidden on mobile to avoid confusion with navigation */}
      {showBreadcrumbs && !isMobile && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <Breadcrumbs />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 ${className}`}>
        {children}
      </main>

      {/* Footer */}
      {showFooter && (
        <Footer 
          companyInfo={defaultCompanyInfo}
          socialLinks={defaultSocialLinks}
          contactInfo={defaultContactInfo}
        />
      )}
    </div>
  );
}
